import { useState } from "react"
import { PlusCircle, Shield } from "lucide-react"
import { Button } from "../components/ui/button"
import { SchoolSidebar } from "../components/ui/school/school-dashboard/school-sidebar"
import { AdminStats } from "../components/ui/school/admin-management/admin-stats"
import { AdminTable } from "../components/ui/school/admin-management/admin-table"
import { AddAdminModal } from "../components/ui/school/admin-management/add-admin-modal"
import { EditAdminModal } from "../components/ui/school/admin-management/edit-admin-modal"
import { DeleteConfirmModal } from "../components/ui/school/admin-management/delete-confirm-modal"
import { initialAdmins } from "../lib/admin-data"

const AdminManagementPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [admins, setAdmins] = useState(initialAdmins)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [deleteAdminId, setDeleteAdminId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  // Calculate stats
  const totalAdmins = admins.length
  const activeAdmins = admins.filter((a) => a.status === "Active").length
  const totalRoles = new Set(admins.map((a) => a.role)).size

  // Pagination
  const adminsPerPage = 10
  const totalPages = Math.ceil(totalAdmins / adminsPerPage)
  const startIndex = (currentPage - 1) * adminsPerPage
  const endIndex = startIndex + adminsPerPage
  const currentAdmins = admins.slice(startIndex, endIndex)

  const handleAddAdmin = (newAdmin) => {
    const admin = {
      ...newAdmin,
      id: (admins.length + 1).toString(),
    }
    setAdmins([...admins, admin])
  }

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin)
    setIsEditModalOpen(true)
  }

  const handleUpdateAdmin = (updatedAdmin) => {
    setAdmins(admins.map((a) => (a.id === updatedAdmin.id ? updatedAdmin : a)))
  }

  const handleDeleteClick = (id) => {
    setDeleteAdminId(id)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = () => {
    setAdmins(admins.filter((a) => a.id !== deleteAdminId))
    setIsDeleteModalOpen(false)
    setDeleteAdminId("")
  }

  const adminToDelete = admins.find((a) => a.id === deleteAdminId)

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SchoolSidebar/>
      {/* <SchoolSidebar isOpen={isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} /> */}

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <main className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : ""}`}>

        {/* Main Content */}
        <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-secondary mb-2">Admin Management</h1>
              <p className="text-muted-foreground">
                Manage staff accounts, roles, permissions, and access levels across the school system.
              </p>
            </div>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle className="w-5 h-5 mr-2" />
              Add Admin
            </Button>
          </div>

          <AdminStats totalAdmins={totalAdmins} activeAdmins={activeAdmins} totalRoles={totalRoles} />

          <AdminTable
            admins={currentAdmins}
            onEdit={handleEditAdmin}
            onDelete={handleDeleteClick}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      {/* Modals */}
      <AddAdminModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onAdd={handleAddAdmin} />

      <EditAdminModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setSelectedAdmin(null)
        }}
        onUpdate={handleUpdateAdmin}
        admin={selectedAdmin}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeleteAdminId("")
        }}
        onConfirm={handleConfirmDelete}
        adminName={adminToDelete?.name || ""}
      />
    </div>
  )
}

export default AdminManagementPage